import { PayloadAction } from '@reduxjs/toolkit';
import { VideoroomErrorPayload } from 'features/streaming/actions';
import { call } from 'redux-saga/effects';
import { SignalingSocket } from 'services/signaling';
import { VideoroomConnectionState } from 'utils/webrtc/ControlledReceivingHandle';
import { RTCClient } from 'utils/webrtc';
import { RejectedJoinCode } from 'utils/webrtc/subscribeGrantError';
import { logger } from 'utils/logger';
import * as Sentry from '@sentry/react';

type ReceiverPlugin = NonNullable<NonNullable<typeof RTCClient.receivingFeed.receiver>['plugin']>;

function* recoverReceiverIfConnecting(
  feed: ReceiverPlugin | undefined,
  active: boolean,
  code: RejectedJoinCode
) {
  if (active && feed && feed.connectionState === VideoroomConnectionState.connecting) {
    yield call(RTCClient.receivingFeed.receiver!.recoverFromRejectedJoin, feed, code);
    return true;
  }
  return false;
}

export function* onVideoroomErrorSaga(action: PayloadAction<VideoroomErrorPayload>) {
  const {
    message: { error_code: errorCode, error },
    handle,
    source,
  } = action.payload;

  const receiverFeed = RTCClient.receivingFeed.receiver?.plugin;
  const isReceiverFeedActive =
    source === 'receiver' && !!receiverFeed && receiverFeed.handle === handle;

  // Suppress Sentry capture only on the proven-benign path: the handled case
  // actually applied (receiver feed in `connecting` got reset). Every other
  // variant — publisher-side error, receiver in a different state, unhandled
  // error code — still reports so we don't mask real bugs.
  let handledBenignPath = false;

  switch (errorCode) {
    case 428: {
      yield call(
        logger.remote({ capture: 'streaming' }).error,
        `Couldn't subscribe in feed ${handle}, maybe there's no such publisher? Original: ${error}`
      );

      handledBenignPath = yield call(
        recoverReceiverIfConnecting,
        receiverFeed,
        isReceiverFeedActive,
        428
      );

      // The awaited recovery can let a `Client.reset` null out or replace the receiver, so re-read it live.
      const liveReceiver = RTCClient.receivingFeed.receiver;
      if (
        isReceiverFeedActive &&
        liveReceiver?.plugin === receiverFeed &&
        action.payload.eventBody?.transaction
      ) {
        yield call(
          liveReceiver.retrySubscription,
          receiverFeed.handle,
          action.payload.eventBody.transaction,
          error
        );
      }

      break;
    }
    case 424: {
      yield call(
        logger.remote({ capture: 'streaming' }).error,
        `Error in ${handle}, receiving room is unconfigured? Original: ${error}`
      );

      handledBenignPath = yield call(
        recoverReceiverIfConnecting,
        receiverFeed,
        isReceiverFeedActive,
        424
      );

      break;
    }

    case 437: {
      yield call(
        logger.remote({ capture: 'streaming' }).error,
        `Error in ${handle}, can't offer SDP with no stream (stale feed?). Original: ${error}`
      );

      handledBenignPath = yield call(
        recoverReceiverIfConnecting,
        receiverFeed,
        isReceiverFeedActive,
        437
      );

      break;
    }

    case 433: {
      yield call(
        logger.remote({ capture: 'streaming' }).error,
        `Error in feed ${handle}. Wrong room pin. Original: ${error}`
      );
      break;
    }

    case 426: {
      yield call(SignalingSocket.send, {
        event: 'roomNotFound',
        data: { type: 'videoroom', id: RTCClient.roomId },
      });

      break;
    }

    default: {
      yield call(
        logger.remote({ capture: 'streaming' }).error,
        `Error in feed ${handle}. Original: ${error}`
      );
    }
  }

  if (!handledBenignPath) {
    Sentry.captureException(new Error(`[${errorCode}]: ${error}`), {
      tags: {
        source,
        error_code: String(errorCode),
      },
      contexts: {
        videoroomError: { handle },
      },
      fingerprint: ['videoroom-error', source, String(errorCode)],
    });
  }
}
